/**
 * shared/components/PageHeader.jsx
 * ---------------------------------
 * Integrated page header — the canonical replacement for the legacy
 * `<FloatingBackButton />` floating pill (v2.71.x).
 *
 * RENDERS:
 *   [← Back/Label]   [Title  (subtitle)]                       [Actions]
 *
 * GUARANTEES:
 *  - Inline, structurally attached to the page (no fixed/absolute floats).
 *  - Compact heights: h-12 (48px) on mobile, h-14 (56px) on desktop.
 *  - 44×44 touch target on the back trigger (a11y).
 *  - Plays nicely inside sticky wrappers — the parent decides stickiness.
 *  - Uses `window.history.back()` when there's deep history, else falls
 *    back to a configurable `home` URL.
 *
 * USAGE
 * -----
 *  Simple:
 *    <PageHeader title="Mumbai Listings" />
 *
 *  Inline with actions:
 *    <PageHeader
 *      title="Property"
 *      subtitle="3BHK in Noida"
 *      actions={<Button size="sm">Edit</Button>}
 *    />
 *
 *  Custom back label:
 *    <PageHeader backLabel="All listings" title="Riverside Heights" />
 *
 *  No back arrow (for top-level pages):
 *    <PageHeader showBack={false} title="Dashboard" />
 *
 *  Custom back behaviour:
 *    <PageHeader onBack={() => nav("/listings")} />
 *
 * v2.71.51 — Initial. Replaces FloatingBackButton.
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";

export default function PageHeader({
  title,
  subtitle = null,
  actions = null,
  showBack = true,
  backLabel = "Back",
  onBack = null,
  home = "/",
  borderless = false,
  className = "",
}) {
  const nav = useNavigate();

  const handleBack = () => {
    if (onBack) { onBack(); return; }
    if (typeof window !== "undefined" && window.history.length > 1) {
      window.history.back();
    } else {
      nav(home);
    }
  };

  return (
    <div
      data-testid="page-header"
      className={`flex items-center gap-2 sm:gap-3 h-12 sm:h-14 ${
        borderless ? "" : "border-b border-slate-200"
      } bg-white ${className}`}
    >
      {showBack && (
        <button
          type="button"
          onClick={handleBack}
          data-testid="page-header-back"
          aria-label={`Back to ${backLabel}`}
          className="
            shrink-0 inline-flex items-center gap-1
            h-9 sm:h-10 -ml-1.5 sm:-ml-2 pl-1.5 pr-2.5
            rounded-md text-slate-600 hover:text-slate-900 hover:bg-slate-100
            active:bg-slate-200 transition-colors
            text-[13px] sm:text-[13.5px] font-medium
          "
        >
          <ChevronLeft size={18} strokeWidth={2.25} className="shrink-0" />
          <span className="hidden sm:inline">{backLabel}</span>
        </button>
      )}

      <div className="flex-1 min-w-0">
        {title && (
          <div
            data-testid="page-header-title"
            className="text-[14px] sm:text-[15px] font-semibold text-slate-900 leading-tight truncate"
          >
            {title}
          </div>
        )}
        {subtitle && (
          <div
            data-testid="page-header-subtitle"
            className="text-[11.5px] sm:text-[12px] text-slate-500 leading-tight truncate mt-0.5"
          >
            {subtitle}
          </div>
        )}
      </div>

      {actions && (
        <div className="shrink-0 flex items-center gap-1.5" data-testid="page-header-actions">
          {actions}
        </div>
      )}
    </div>
  );
}
